// SPDX-FileCopyrightText: 2024 LiveKit, Inc.
//
// SPDX-License-Identifier: Apache-2.0
import type LocalTrack from './LocalTrack.ts';

const isMediaRecorderAvailable = typeof MediaRecorder !== 'undefined';

class FallbackRecorder {
	constructor() {
		throw new Error('MediaRecorder is not available in this environment');
	}
}

const RecorderBase = isMediaRecorderAvailable ? MediaRecorder : (FallbackRecorder as unknown as typeof MediaRecorder);

export class LocalTrackRecorder<T extends LocalTrack> extends RecorderBase {
	byteStream: ReadableStream<Uint8Array>;

	constructor(track: T, options?: MediaRecorderOptions) {
		if (!isMediaRecorderAvailable) {
			throw new Error('MediaRecorder is not available in this environment');
		}

		super(new MediaStream([track.mediaStreamTrack]), options);

		let dataListener: (event: BlobEvent) => void;

		let streamController: ReadableStreamDefaultController<Uint8Array> | undefined;

		const isClosed = () => streamController === undefined;

		const onStop = () => {
			this.removeEventListener('dataavailable', dataListener);
			this.removeEventListener('stop', onStop);
			this.removeEventListener('error', onError);
			streamController?.close();
			streamController = undefined;
		};

		const onError = (event: Event) => {
			streamController?.error(event);
			this.removeEventListener('dataavailable', dataListener);
			this.removeEventListener('stop', onStop);
			this.removeEventListener('error', onError);
			streamController = undefined;
		};

		this.byteStream = new ReadableStream({
			start: (controller) => {
				streamController = controller;
				dataListener = async (event: BlobEvent) => {
					let data: Uint8Array;

					if (event.data.arrayBuffer) {
						const arrayBuffer = await event.data.arrayBuffer();
						data = new Uint8Array(arrayBuffer);
					} else if ('byteArray' in event.data) {
						data = (event.data as unknown as {byteArray: Uint8Array}).byteArray;
					} else {
						throw new Error('no data available!');
					}

					if (isClosed()) {
						return;
					}
					controller.enqueue(data);
				};
				this.addEventListener('dataavailable', dataListener);
			},
			cancel: () => {
				onStop();
			},
		});

		this.addEventListener('stop', onStop);
		this.addEventListener('error', onError);
	}
}

export function isRecordingSupported(): boolean {
	return isMediaRecorderAvailable;
}
